import axios from "axios";
import { supabase } from "./supabaseClient";

// Google Play Billing bridge for the RoadRnr Android WebView wrapper. Normal
// browser/web users never hit this path (they keep using Stripe). Inside the
// wrapper, purchases are sent as { type: "google_play_purchase", productId, ... }
// and the native side reports the result back via window.__roadrnrNativeBillingMessage.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stripe listing price id (or null = single £1.99 listing) → Google Play product id.
const STRIPE_PRICE_TO_GP = {
  price_1SxOTpCFqL4V7hfxuSqUZhOh: "add_event_1",
  price_1UDU0QIVUBblOsLnJCaeqtGq: "listing_pack_10",
  price_1UDU1hIVUBblOsLnKCmhN0Oi: "listing_pack_20",
};

export function listingProductId(priceId) {
  if (!priceId) return "add_event_1";
  return STRIPE_PRICE_TO_GP[priceId] || "add_event_1";
}

// Stripe listing price id (or null = single £1.99 listing) → Apple IAP product id.
const STRIPE_PRICE_TO_APPLE = {
  price_1SxOTpCFqL4V7hfxuSqUZhOh: "app.roadrnr.add_event_1",
  price_1UDU0QIVUBblOsLnJCaeqtGq: "app.roadrnr.listing_pack_10",
  price_1UDU1hIVUBblOsLnKCmhN0Oi: "app.roadrnr.listing_pack_20",
};

export function listingAppleProductId(priceId) {
  if (!priceId) return "app.roadrnr.add_event_1";
  return STRIPE_PRICE_TO_APPLE[priceId] || "app.roadrnr.add_event_1";
}

// True only inside the RoadRnr Android wrapper.
export function isNativeAndroid() {
  if (typeof window === "undefined") return false;
  if (window.__roadrnrNativeIOS) return false;
  if (window.__roadrnrNative === true) return true;
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const hasBridge =
    !!(window.ReactNativeWebView && window.ReactNativeWebView.postMessage) ||
    !!(window.__roadrnrNativeBilling && window.__roadrnrNativeBilling.postMessage) ||
    !!(window.AndroidBilling && window.AndroidBilling.postMessage) ||
    !!(window.Android && window.Android.postMessage);
  return hasBridge || /RoadRnr/i.test(ua);
}

// True only inside the RoadRnr iOS (WKWebView) wrapper. Checked BEFORE the
// Android detector by callers, since both may see "RoadRnr" in the UA.
export function isNativeIOS() {
  if (typeof window === "undefined") return false;
  if (window.__roadrnrNativeIOS) return true;
  const mh = window.webkit && window.webkit.messageHandlers;
  const hasBridge = !!(mh && (mh.roadrnrBilling || mh.__roadrnrNativeBilling));
  if (hasBridge) return true;
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isApple = /iPad|iPhone|iPod/.test(ua);
  return isApple && /RoadRnr/i.test(ua);
}

function postToNative(payload) {
  const json = JSON.stringify(payload);
  if (window.ReactNativeWebView?.postMessage) return window.ReactNativeWebView.postMessage(json);
  if (window.__roadrnrNativeBilling?.postMessage) return window.__roadrnrNativeBilling.postMessage(json);
  if (window.AndroidBilling?.postMessage) return window.AndroidBilling.postMessage(json);
  if (window.Android?.postMessage) return window.Android.postMessage(json);
  throw new Error("Native billing is unavailable.");
}

// iOS WKWebView message handlers take a JS object; fall back to generic bridges.
function postToNativeIOS(payload) {
  const json = JSON.stringify(payload);
  if (window.ReactNativeWebView?.postMessage) return window.ReactNativeWebView.postMessage(json);
  const mh = window.webkit && window.webkit.messageHandlers;
  if (mh?.roadrnrBilling?.postMessage) return mh.roadrnrBilling.postMessage(payload);
  if (mh?.__roadrnrNativeBilling?.postMessage) return mh.__roadrnrNativeBilling.postMessage(payload);
  if (window.__roadrnrNativeBilling?.postMessage) return window.__roadrnrNativeBilling.postMessage(json);
  throw new Error("Native billing is unavailable.");
}

const _pending = new Map(); // requestId -> { resolve }
let _installed = false;

// Route a native billing result to its pending purchase. Accepts the raw value
// from any inbound channel (direct global call OR a postMessage/MessageEvent).
function _resolveNativeMessage(raw) {
  let msg = raw;
  if (typeof raw === "string") {
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
  }
  if (!msg || typeof msg !== "object") return;
  // Only treat this as a billing result — never resolve on unrelated window
  // "message" traffic (auth, wallets, dev tooling, iframes, etc.).
  const looksLikeResult =
    "success" in msg ||
    "canceled" in msg ||
    "error" in msg ||
    "purchaseToken" in msg ||
    "receipt" in msg ||
    "transactionId" in msg;
  let entry = msg.requestId ? _pending.get(msg.requestId) : null;
  if (!entry) {
    if (!looksLikeResult) return;
    if (_pending.size === 1) entry = [..._pending.values()][0];
  }
  if (!entry) return;
  if (msg.requestId && _pending.has(msg.requestId)) _pending.delete(msg.requestId);
  else _pending.clear();
  entry.resolve(msg);
}

function installReceiver() {
  if (_installed) return;
  _installed = true;
  // Primary channel: the native wrapper injects a call to this global with the
  // billing result (object or JSON string):
  // { requestId?, productId, success, purchaseToken?, receipt?, transactionId?, orderId?, canceled?, error? }
  window.__roadrnrNativeBillingMessage = _resolveNativeMessage;
  // Fallback channels: some native shells deliver the result as a postMessage /
  // MessageEvent instead of calling the global (React Native WebView dispatches a
  // "message" event on window on iOS and on document on Android).
  const onEvent = (e) => _resolveNativeMessage(e && e.data);
  try {
    window.addEventListener("message", onEvent);
  } catch {
    /* ignore */
  }
  try {
    document.addEventListener("message", onEvent);
  } catch {
    /* ignore */
  }
}

function purchaseNative(productId, context = {}) {
  installReceiver();
  const requestId = `${productId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (_pending.has(requestId)) {
        _pending.delete(requestId);
        reject(new Error("Purchase timed out. Please try again."));
      }
    }, 180000);
    _pending.set(requestId, {
      resolve: (v) => {
        clearTimeout(timer);
        resolve(v);
      },
    });
    try {
      postToNative({ type: "google_play_purchase", productId, requestId, context });
    } catch (e) {
      clearTimeout(timer);
      _pending.delete(requestId);
      reject(e);
    }
  });
}

// Apple IAP purchase over the same native bridge/receiver pattern. The iOS
// wrapper reports the result back via window.__roadrnrNativeBillingMessage:
// { requestId?, productId, success, receipt, transactionId, orderId?, canceled?, error? }
function purchaseNativeApple(productId, context = {}) {
  installReceiver();
  const requestId = `${productId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve, reject) => {
    let timer = null;
    _pending.set(requestId, {
      resolve: (v) => {
        if (timer) clearTimeout(timer);
        resolve(v);
      },
    });
    try {
      // Send the Apple purchase to the native shell (via ReactNativeWebView first).
      postToNativeIOS({ type: "apple_iap_purchase", productId, requestId, context });
    } catch (e) {
      _pending.delete(requestId);
      reject(e);
      return;
    }
    // Start the 180s timeout ONLY after the Apple message was posted to native.
    timer = setTimeout(() => {
      if (_pending.has(requestId)) {
        _pending.delete(requestId);
        reject(new Error("Purchase timed out. Please try again."));
      }
    }, 180000);
  });
}

async function authHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function verifyGooglePlay({ productId, purchaseToken, context }) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/google-play/verify`,
    { productId, purchaseToken, packageName: "app.roadrnr", context: context || {} },
    { headers }
  );
  return data;
}

// Buy via Google Play, then verify + grant the entitlement server-side.
// Resolves on success; throws on cancel/error.
export async function runNativePurchase(productId, context = {}) {
  const res = await purchaseNative(productId, context);
  if (res?.canceled) {
    const e = new Error("Purchase cancelled.");
    e.canceled = true;
    throw e;
  }
  if (!res?.success || !res?.purchaseToken) {
    throw new Error(res?.error || "Purchase did not complete.");
  }
  const verify = await verifyGooglePlay({ productId, purchaseToken: res.purchaseToken, context });
  if (!verify?.ok) throw new Error("Could not verify your purchase.");
  return verify;
}

async function verifyApple({ productId, receipt, transactionId, context }) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/apple-iap/verify`,
    { productId, receipt, transactionId, bundleId: "app.roadrnr", context: context || {} },
    { headers }
  );
  return data;
}

// Buy via Apple IAP, then verify + grant the entitlement server-side.
// Resolves on success; throws on cancel/error.
export async function runNativeApplePurchase(productId, context = {}) {
  const res = await purchaseNativeApple(productId, context);
  if (res?.canceled) {
    const e = new Error("Purchase cancelled.");
    e.canceled = true;
    throw e;
  }
  if (!res?.success || !res?.receipt) {
    throw new Error(res?.error || "Purchase did not complete.");
  }
  const verify = await verifyApple({
    productId,
    receipt: res.receipt,
    transactionId: res.transactionId,
    context,
  });
  if (!verify?.ok) throw new Error("Could not verify your purchase.");
  return verify;
}
